import React, { useEffect, useState } from "react";
import {
  Select,
  SelectItem,
  Image,
  Button,
  addToast,
  Input
} from "@heroui/react";
import BAFTokenImage from "../../images/bafrnd.png";
import EthIcon from "../../images/icon/ETH.png";
import binace from "../../images/binace-black.png";
import dai from "../../images/dai-logo.avif";
import usdt from "../../images/usdt-logo.png";
import TokenSwapTable from "./token-swap-table";
import { useWallet } from "../../context/WalletContext";
import swapTokens from "../token-swap/common";
import { formatBalance, getAmountsOut } from "../../utils/common";
import {
  TokenSwapApi,
  TokenSwapListApi
} from "../../../src/api/methods-marketplace";
import { getCookies } from "../../utils/cookies";
import { useSelector } from "react-redux";
import { isUserLoggedIn } from "../../redux/reducer/user_reducer";
import { useNavigate } from "react-router-dom";

export const tokenList = [
  {
    id: 1,
    name: "BAF",
    avatar: BAFTokenImage
  },
  {
    id: 2,
    name: "tBNB",
    avatar: binace
  },
  {
    id: 3,
    name: "ETH",
    avatar: EthIcon
  },
  {
    id: 4,
    name: "DAI",
    avatar: dai
  },
  {
    id: 5,
    name: "USDT",
    avatar: usdt
  }
];

const columns = [
  {
    uid: "id",
    label: "ID"
  },
  {
    uid: "created_at",
    label: "Date"
  },
  {
    uid: "exchange_rate",
    label: "Exchange Rate"
  },
  {
    uid: "swap",
    label: "Swap"
  },
  {
    uid: "status",
    label: "Status"
  },
  {
    uid: "transaction_hash",
    label: "View Txn"
  }
];

const TokenSwapComponent = () => {
  const [selectedToken, setSelectedToken] = useState(1);
  const [selectedTokenTo, setSelectedTokenTo] = useState(2);
  const [userInput, setUserInput] = useState("");
  const [convertedAmount, setConvertedAmount] = useState(0);
  const [status, setStatus] = useState("");
  const [transactionHash, setTransactionHash] = useState("");
  const [blockNumber, setBlockNumber] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = getCookies();
  const { user } = useSelector((state) => state);
  const isLoggedIn = useSelector(isUserLoggedIn);
  const navigate = useNavigate();

  const {
    isConnected,
    address,
    connectWallet,
    bafBalanceFormatted,
    tokenBalances,
    refreshAllBalances,
    hasAddressMismatch,
    disconnectWallet,
    isCustodialWallet,
    publicClient
  } = useWallet();

  const WBNB_TESTNET = process.env.REACT_APP_WBNB_TESTNET;
  const USDT_TESTNET = process.env.REACT_APP_USDT_TESTNET;
  const BAF_TESTNET = process.env.REACT_APP_BAF_TESTNET;
  const DAI_TESTNET = process.env.REACT_APP_DAI_TESTNET;
  const ETH_TESTNET = process.env.REACT_APP_ETH_TESTNET;
  const TBNB_TESTNET = process.env.REACT_APP_WBNB_TESTNET;

  const handleDisconnect = () => {
    disconnectWallet();
  };
  useEffect(() => {
    if (!user?.login) {
      handleDisconnect();
    }
  }, [user]);

  useEffect(() => {
    if (!isConnected) {
      setUserInput("");
    }
  }, [isConnected]);

  const getSwapHistory = async () => {
    try {
      setLoading(true);
      const historyData = await TokenSwapListApi();
      if (historyData?.data?.data) {
        setHistory(historyData.data.data);
      }
    } catch (error) {
      console.error(
        "Error fetching swap history:",
        error.message || "Unknown error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSwapHistory();
  }, []);

  const balanceList = [
    {
      name: "BAF",
      value: bafBalanceFormatted ? parseFloat(bafBalanceFormatted) : 0,
      testnet_address: BAF_TESTNET
    },
    {
      name: "ETH",
      value: tokenBalances.eth ? parseFloat(tokenBalances.eth) : 0,
      testnet_address: ETH_TESTNET
    },
    {
      name: "WBNB",
      value: tokenBalances.wbnb ? parseFloat(tokenBalances.wbnb) : 0,
      testnet_address: WBNB_TESTNET
    },
    {
      name: "USDT",
      value: tokenBalances.usdt ? parseFloat(tokenBalances.usdt) : 0,
      testnet_address: USDT_TESTNET
    },
    {
      name: "DAI",
      value: tokenBalances.dai ? parseFloat(tokenBalances.dai) : 0,
      testnet_address: DAI_TESTNET
    },
    {
      name: "tBNB",
      value: tokenBalances.bnb ? parseFloat(tokenBalances.bnb) : 0,
      testnet_address: TBNB_TESTNET
    }
  ];

  const selectedTokenSymbol =
    tokenList.find((item) => item.id === Number(selectedToken))?.name || "";

  const selectedTokenid = tokenList.find(
    (item) => item.id === Number(selectedToken)
  )?.id;

  const selectedTokenToSymbol =
    tokenList.find((item) => item.id === Number(selectedTokenTo))?.name || "";

  const selectedTokenToId = tokenList.find(
    (item) => item.id === Number(selectedTokenTo)
  )?.id;

  const selectedTokenBalance =
    balanceList.find((item) => item.name === selectedTokenSymbol)?.value || 0;

  const selectedTokenToBalance =
    balanceList.find((item) => item.name === selectedTokenToSymbol)?.value || 0;

  const selectedTokenToAddress = balanceList.find(
    (item) => item.name === selectedTokenToSymbol
  )?.testnet_address;

  const selectedTokenAddress = balanceList.find(
    (item) => item.name === selectedTokenSymbol
  )?.testnet_address;

  // Fetch conversion rate when input or tokens change
  useEffect(() => {
    const fetchRate = async () => {
      if (!userInput || !selectedTokenAddress || !selectedTokenToAddress) {
        setConvertedAmount(0);
        return;
      }

      try {
        const result = await getAmountsOut(
          userInput,
          selectedTokenAddress,
          selectedTokenToAddress
        );
        setConvertedAmount(result);
      } catch (err) {
        console.error("Error fetching conversion rate:", err);
        setConvertedAmount("0");
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(fetchRate, 300);
    return () => clearTimeout(timeoutId);
  }, [userInput, selectedTokenAddress, selectedTokenToAddress]);

  const swapField = () => {
    const temp = selectedTokenTo;
    setSelectedToken(temp);
    const temp2 = selectedToken;
    setSelectedTokenTo(temp2);
    setUserInput("");
    setConvertedAmount(0);
  };

  const handleSwap = async () => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    if (!isConnected) {
      connectWallet();
      return;
    }

    if ((!isConnected || !address) && isLoggedIn) {
      addToast({
        title: "Wallet Not Connected",
        description: "Please connect your wallet to proceed with the swap.",
        status: "warning",
        variant: "bordered",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if ((!user?.data?.address && address) || hasAddressMismatch) {
      addToast({
        title: !hasAddressMismatch ? "Profile Incomplete" : "Address Mismatch",
        description: !hasAddressMismatch
          ? "Wallet address belongs to another account. Please reconnect with the correct one."
          : "The connected wallet address does not match your registered address. Please reconnect with the correct one.",
        color: "warning",
        variant: "bordered",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (!userInput || parseFloat(userInput) <= 0) {
      addToast({
        title: "Invalid Amount",
        description: "Please enter a valid amount to swap.",
        status: "warning",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    if (parseFloat(userInput) > selectedTokenBalance) {
      addToast({
        title: "Insufficient Balance",
        description: `You only have ${selectedTokenBalance} ${selectedTokenSymbol}`,
        status: "warning",
        duration: 3000,
        isClosable: true
      });
      return;
    }
    setTransactionHash("");
    setBlockNumber("");

    try {
      const result = await swapTokens(
        userInput,
        selectedTokenAddress,
        selectedTokenToAddress,
        address,
        selectedTokenSymbol,
        selectedTokenToSymbol,
        convertedAmount,
        token,
        blockNumber,
        transactionHash,
        setStatus,
        setTransactionHash,
        setBlockNumber,
        getSwapHistory,
        isCustodialWallet, // Add this
        publicClient // Add this (optional, for getting block number)
      );

      if (result?.success === 200 && result?.txHash) {
        const updatedData = {
          from_token: selectedTokenAddress,
          to_token: selectedTokenToAddress,
          from_symbol: selectedTokenSymbol,
          to_symbol: selectedTokenToSymbol,
          from_amount: userInput,
          to_amount: convertedAmount,
          id: result?.blockNumber || blockNumber,
          transaction_hash: result?.txHash,
          status: "Success",
          swap_rate: parseFloat(convertedAmount) / parseFloat(userInput)
        };

        const response = await TokenSwapApi(updatedData, token);

        if (response?.data?.success) {
          addToast({
            title: "Swap Successful",
            description: `Successfully swapped ${userInput} ${selectedTokenSymbol} to ${formatBalance(
              convertedAmount
            )} ${selectedTokenToSymbol}`,
            color: "success",
            variant: "bordered"
          });

          setUserInput("");
          setConvertedAmount(0);

          await refreshAllBalances();
          await getSwapHistory();
        }
      } else {
        const updatedData = {
          from_token: selectedTokenAddress,
          to_token: selectedTokenToAddress,
          from_symbol: selectedTokenSymbol,
          to_symbol: selectedTokenToSymbol,
          from_amount: userInput,
          to_amount: convertedAmount,
          id: result?.blockNumber || blockNumber,
          transaction_hash: result?.txHash,
          status: "Failed",
          swap_rate: parseFloat(convertedAmount) / parseFloat(userInput)
        };

        const response = await TokenSwapApi(updatedData, token);

        if (response?.data?.success) {
          addToast({
            title: "Swap Failed",
            description: `Failed swap ${userInput} ${selectedTokenSymbol} to ${formatBalance(
              convertedAmount
            )} ${selectedTokenToSymbol}`,
            color: "warning",
            variant: "bordered"
          });

          setUserInput("");
          setConvertedAmount(0);

          await refreshAllBalances();
          await getSwapHistory();
        }
      }
    } catch (error) {
      console.error("❌ Swap error:", error);

      const updatedData = {
        from_token: selectedTokenAddress,
        to_token: selectedTokenToAddress,
        from_symbol: selectedTokenSymbol,
        to_symbol: selectedTokenToSymbol,
        from_amount: userInput,
        to_amount: convertedAmount,
        id: blockNumber,
        tx_hash: transactionHash,
        status: "Failed",
        swap_rate: parseFloat(convertedAmount) / parseFloat(userInput)
      };

      await TokenSwapApi(updatedData, token);

      addToast({
        title: "Swap Failed",
        description: error?.message || "An error occurred during the swap.",
        status: "error",
        duration: 3000,
        isClosable: true
      });

      setUserInput("");
      setConvertedAmount(0);
      await refreshAllBalances();
      await getSwapHistory();
    }
  };

  const handleSelectToken = (event) => {
    const value = event?.target?.value || event?.key;
    setSelectedToken(value);
    setUserInput("");
    setConvertedAmount(0);
  };

  const handleSelectTokenTo = (event) => {
    const value = event?.target?.value;
    setSelectedTokenTo(value);
    setUserInput("");
    setConvertedAmount(0);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;

    if (!isConnected) {
      addToast({
        title: "Wallet Not Connected",
        description: "Please connect your wallet to proceed.",
        status: "warning",
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setStatus("");

    // Allow only numbers and decimal point
    const regex = /^\d*\.?\d*$/;
    if (!regex.test(value)) return;

    // Prevent values that exceed balance
    const numericValue = parseFloat(value);
    if (value && numericValue > selectedTokenBalance) {
      addToast({
        title: "Exceeds Available Balance",
        description: `You only have ${formatBalance(
          selectedTokenBalance
        )} ${selectedTokenSymbol}`,
        status: "warning",
        duration: 2000,
        isClosable: true
      });
      return;
    }

    setUserInput(value);
  };

  const handleMaxClick = () => {
    if (selectedTokenBalance > 0) {
      setUserInput(selectedTokenBalance.toString());
    }
  };

  const TokenSwapTableLoader = () => {
    return (
      <div className="flex flex-col gap-2 w-full py-6 px-8">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="grid grid-cols-6 gap-2 items-center animate-pulse"
          >
            <div className="h-4 bg-gray-300 rounded w-12" />
            <div className="h-4 bg-gray-300 rounded w-24" />
            <div className="h-4 bg-gray-300 rounded w-20" />
            <div className="h-4 bg-gray-300 rounded w-40" />
            <div className="h-10 bg-gray-300 rounded-full w-28 ml-20" />
            <div className="h-4 bg-gray-300 rounded w-28 ml-5" />
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="py-8 lg:py-20 px-4">
        <div className="max-w-[28rem] mx-auto flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <div className="bg-[#EBEBEB] rounded-xl py-3 px-4">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <Select
                    className="max-w-[8rem]"
                    classNames={{
                      base: "data-[has-label=true]:mt-0",
                      label: "hidden group-data-[filled=true]:-translate-y-0",
                      trigger:
                        "h-10 border-medium border-black data-[hover=true]:border-black data-[open=true]:border-black data-[focus=true]:border-black bg-black text-white",
                      listboxWrapper: "max-h-[400px]"
                    }}
                    items={tokenList}
                    label="select token"
                    placeholder="select token"
                    disabledKeys={[
                      selectedTokenToId?.toString(),
                      selectedTokenid?.toString()
                    ]}
                    labelPlacement="outside"
                    listboxProps={{
                      itemClasses: {
                        base: [
                          "rounded-md",
                          "text-default-500",
                          "transition-opacity",
                          "data-[hover=true]:text-foreground",
                          "data-[hover=true]:bg-default-100",
                          "dark:data-[hover=true]:bg-default-50",
                          "data-[selectable=true]:focus:bg-default-50",
                          "data-[pressed=true]:opacity-70",
                          "data-[focus-visible=true]:ring-default-500"
                        ]
                      }
                    }}
                    popoverProps={{
                      classNames: {
                        base: "before:bg-default-200",
                        content:
                          "p-0 border-small border-divider bg-background shadow-none"
                      }
                    }}
                    renderValue={(items) => {
                      return items.map((item) => (
                        <div key={item?.id} className="flex items-center gap-2">
                          <Image
                            alt={item?.data?.name}
                            src={item?.data?.avatar}
                            radius="none"
                            className="object-contain w-8 h-[auto]"
                          />
                          <div className="flex flex-col">
                            <span className="text-xs text-white">
                              {item?.data?.name}
                            </span>
                          </div>
                        </div>
                      ));
                    }}
                    variant="bordered"
                    selectedKeys={[selectedToken?.toString()]}
                    onChange={handleSelectToken}
                    value={selectedToken}
                  >
                    {(user) => (
                      <SelectItem key={user?.id} textValue={user?.name}>
                        <div className="flex gap-2 items-center">
                          <Image
                            alt={user?.name}
                            src={user?.avatar}
                            radius="none"
                            className="object-contain w-8 h-[auto]"
                          />
                          <div className="flex flex-col">
                            <span className="text-xs">{user?.name}</span>
                          </div>
                        </div>
                      </SelectItem>
                    )}
                  </Select>
                  <div className="m-0 text-lg font-normal text-[#717A8C]">
                    <Input
                      value={userInput}
                      onChange={handleInputChange}
                      placeholder="Enter amount"
                      type="text"
                      inputMode="decimal"
                    />
                  </div>
                </div>
                {isLoggedIn && isConnected && (
                  <div className="flex items-center justify-between">
                    <p className="m-0 text-xs font-normal text-[#717A8C]">
                      Balance: {formatBalance(selectedTokenBalance)}{" "}
                      {selectedTokenSymbol}
                    </p>
                    <button
                      onClick={handleMaxClick}
                      className="text-xs font-medium text-black hover:underline cursor-pointer"
                    >
                      MAX
                    </button>
                  </div>
                )}
              </div>
              <div
                onClick={swapField}
                className="w-10 h-10 sm:w-14 sm:h-14 rounded-full border-4 sm:border-[0.375rem] border-white bg-black flex items-center justify-center mx-auto mb-[-2rem] sm:mb-[-2.75rem] relative z-[1] cursor-pointer hover:bg-gray-800 transition-colors"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 sm:w-6 sm:h-6"
                >
                  <path
                    d="M10.4498 6.71973L6.72974 2.99976L3.00977 6.71973"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6.72949 21L6.72949 3"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.5498 17.28L17.2698 21L20.9898 17.28"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17.2695 3V21"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div className="bg-[#EBEBEB] rounded-xl pb-3 pt-4 sm:pt-3 px-4">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <Select
                    className="max-w-[8rem]"
                    classNames={{
                      base: "data-[has-label=true]:mt-0",
                      label: "hidden group-data-[filled=true]:-translate-y-0",
                      trigger:
                        "h-10 border-medium border-black data-[hover=true]:border-black data-[open=true]:border-black data-[focus=true]:border-black bg-black text-white",
                      listboxWrapper: "max-h-[400px]"
                    }}
                    items={tokenList}
                    label="select token"
                    placeholder="select token"
                    labelPlacement="outside"
                    disabledKeys={[
                      selectedTokenid?.toString(),
                      selectedTokenToId?.toString()
                    ]}
                    listboxProps={{
                      itemClasses: {
                        base: [
                          "rounded-md",
                          "text-default-500",
                          "transition-opacity",
                          "data-[hover=true]:text-foreground",
                          "data-[hover=true]:bg-default-100",
                          "dark:data-[hover=true]:bg-default-50",
                          "data-[selectable=true]:focus:bg-default-50",
                          "data-[pressed=true]:opacity-70",
                          "data-[focus-visible=true]:ring-default-500"
                        ]
                      }
                    }}
                    popoverProps={{
                      classNames: {
                        base: "before:bg-default-200",
                        content:
                          "p-0 border-small border-divider bg-background shadow-none"
                      }
                    }}
                    renderValue={(items) => {
                      return items.map((item) => (
                        <div
                          key={item?.key}
                          className="flex items-center gap-2"
                        >
                          <Image
                            alt={item?.data?.name}
                            src={item?.data?.avatar}
                            radius="none"
                            className="object-contain w-6 h-[auto]"
                          />
                          <div className="flex flex-col">
                            <span className="text-xs text-white">
                              {item?.data?.name}
                            </span>
                          </div>
                        </div>
                      ));
                    }}
                    variant="bordered"
                    selectedKeys={[selectedTokenTo.toString()]}
                    onChange={handleSelectTokenTo}
                    value={selectedTokenTo}
                  >
                    {(user) => (
                      <SelectItem key={user?.id} textValue={user?.name}>
                        <div className="flex gap-2 items-center">
                          <Image
                            alt={user?.name}
                            src={user?.avatar}
                            radius="none"
                            className="object-contain w-6 h-[auto]"
                          />
                          <div className="flex flex-col">
                            <span className="text-xs">{user?.name}</span>
                          </div>
                        </div>
                      </SelectItem>
                    )}
                  </Select>
                  <div className="m-0 text-lg font-normal text-[#717A8C]">
                    <Input
                      isReadOnly
                      labelPlacement="outside"
                      type="text"
                      value={formatBalance(convertedAmount)}
                      placeholder={`Value in ${selectedTokenToSymbol}`}
                    />
                  </div>
                </div>
                {isLoggedIn && isConnected && (
                  <div className="flex items-center justify-between">
                    <p className="m-0 text-xs font-normal text-[#717A8C]">
                      Balance: {formatBalance(selectedTokenToBalance)}{" "}
                      {selectedTokenToSymbol}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          {userInput && convertedAmount > 0 && (
            <div className="result">
              <p className="m-0 text-xs font-normal text-[#717A8C] text-right">
                {userInput} {selectedTokenSymbol} ≈{" "}
                {formatBalance(convertedAmount)} {selectedTokenToSymbol}
              </p>
            </div>
          )}
          {status && (
            <div className="result">
              <p
                className={`m-0 text-xs font-normal ${!status.includes("Transaction failed") ? "text-green-600" : "text-red-600"} text-right`}
              >
                {status}
              </p>
            </div>
          )}
          <Button
            onPress={handleSwap}
            className="bg-yellowbuy border border-black text-base text-black font-normal w-full"
            radius="sm"
            isDisabled={
              (isLoggedIn && isConnected && !userInput) ||
              (isLoggedIn && isConnected && parseFloat(userInput) <= 0) ||
              (convertedAmount <= 0 && isLoggedIn && isConnected)
            }
          >
            {!isLoggedIn
              ? "Login Required"
              : !isConnected
                ? "Connect Wallet"
                : "Swap"}
          </Button>
        </div>
        <div className="mt-12 max-w-7xl mx-auto">
          <div className="title-wrapper mb-6">
            <h1 className="m-0 text-2xl text-black font-medium uppercase">
              Transaction History
            </h1>
          </div>
          <TokenSwapTable
            tableColumns={columns}
            tableData={[...history].reverse()}
            emptyDataContent={
              loading ? (
                <TokenSwapTableLoader />
              ) : !isLoggedIn ? (
                "Please log in to view transaction history."
              ) : (
                "No transaction history found. Start swapping tokens to see your transactions here."
              )
            }
            isLoading={loading}
          />
        </div>
      </div>
    </>
  );
};

export default TokenSwapComponent;
